import {
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuList,
  ModalBody,
  ModalContent,
  ModalHeader,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  type ChainFullId,
  ChainFullIds,
  ChainIds,
  getChainPriority,
  getNativeTokenPriority,
  getTokenSymbol,
  useChainRegistry,
} from "@umami/chains";
import {
  type UnifiedBalanceWithPrices,
  useGetNetworkByFullId,
  useGetNetworkChain,
  useGetTokensAndBalances,
} from "@umami/state";
import { isEtherlinkOnRampEnabled } from "@umami/utils";
import { type ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { ChainIcon } from "../Token/ChainIcon";
import { TokenMultichain } from "../Token/Token";

import { AllNetworksIcon, ChevronDownIcon, SearchIcon } from "@/assets/icons";
import { ModalBackButton } from "@/components/BackButton";
import { ModalCloseButton } from "@/components/CloseButton";
import { type TokenWithAddress, fetchPopularTokens, searchTokens } from "@/services/tokenService";
import { useColor } from "@/styles/useColor";
import { DEFAULT_RAMP_TOKENS } from "@/utils/defaultTokens";

type Network = "All networks" | "Ethereum" | "Etherlink" | "BSC" | "Tezos";

interface NetworkConfig {
  id: Network;
  label: string;
  icon: ReactElement;
}

const NETWORK_CONFIGS: NetworkConfig[] = [
  {
    id: "All networks",
    label: "All networks",
    icon: <AllNetworksIcon width="20px" height="20px" />,
  },
  {
    id: "Tezos",
    label: "Tezos",
    icon: <ChainIcon boxSize="20px" chainId={ChainIds.TEZOS} />,
  },
  {
    id: "Ethereum",
    label: "Ethereum",
    icon: <ChainIcon boxSize="20px" chainId={ChainIds.ETHEREUM} />,
  },
  ...(isEtherlinkOnRampEnabled()
    ? [
        {
          id: "Etherlink" as Network,
          label: "Etherlink",
          icon: <ChainIcon boxSize="20px" chainId={ChainIds.ETHERLINK} />,
        },
      ]
    : []),
];

type AssetSelectModalProps = {
  onClick: (tokenName: string, tokenChain: ChainFullId, iconUrl?: string) => void;
};

export const AssetSelectModal = ({ onClick }: AssetSelectModalProps) => {
  const { balances, tokens } = useGetTokensAndBalances();
  const [searchQuery, setSearchQuery] = useState("");
  const [dynamicTokens, setDynamicTokens] = useState<TokenWithAddress[]>([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [resolvedIconUrls, setResolvedIconUrls] = useState<Map<string, string>>(new Map());
  const color = useColor();
  const { registry } = useChainRegistry();
  const getNetworkChain = useGetNetworkChain();
  const getNetworkConfig = useGetNetworkByFullId();

  const [selectedNetwork, setSelectedNetwork] = useState<Network>("All networks");

  useEffect(() => {
    const loadDynamicTokens = async () => {
      if (
        selectedNetwork === "All networks" ||
        selectedNetwork === "Ethereum" ||
        selectedNetwork === "Etherlink"
      ) {
        setIsLoadingTokens(true);
        try {
          const tokens: TokenWithAddress[] = [];

          if (selectedNetwork === "All networks" || selectedNetwork === "Ethereum") {
            const ethereumTokens = await fetchPopularTokens(ChainFullIds.ETHEREUM);
            tokens.push(...ethereumTokens);
          }

          if (selectedNetwork === "All networks" || selectedNetwork === "Etherlink") {
            const etherlinkTokens = await fetchPopularTokens(ChainFullIds.ETHERLINK);
            tokens.push(...etherlinkTokens);
          }

          setDynamicTokens(tokens);
        } catch (error) {
          console.error("Error loading dynamic tokens:", error);
        } finally {
          setIsLoadingTokens(false);
        }
      } else {
        setDynamicTokens([]);
      }
    };

    void loadDynamicTokens();
  }, [selectedNetwork]);

  useEffect(() => {
    const searchDynamicTokens = async () => {
      if (
        searchQuery.length >= 2 &&
        (selectedNetwork === "All networks" ||
          selectedNetwork === "Ethereum" ||
          selectedNetwork === "Etherlink")
      ) {
        setIsLoadingTokens(true);
        try {
          const searchResults: TokenWithAddress[] = [];

          if (selectedNetwork === "All networks" || selectedNetwork === "Ethereum") {
            const ethereumResults = await searchTokens(searchQuery, ChainFullIds.ETHEREUM);
            searchResults.push(...ethereumResults);
          }

          if (selectedNetwork === "All networks" || selectedNetwork === "Etherlink") {
            const etherlinkResults = await searchTokens(searchQuery, ChainFullIds.ETHERLINK);
            searchResults.push(...etherlinkResults);
          }

          setDynamicTokens(searchResults);
        } catch (error) {
          console.error("Error searching tokens:", error);
        } finally {
          setIsLoadingTokens(false);
        }
      } else if (
        searchQuery.length === 0 &&
        (selectedNetwork === "All networks" ||
          selectedNetwork === "Ethereum" ||
          selectedNetwork === "Etherlink")
      ) {
        // Load popular tokens when search is cleared
        setIsLoadingTokens(true);
        try {
          const tokens: TokenWithAddress[] = [];

          if (selectedNetwork === "All networks" || selectedNetwork === "Ethereum") {
            const ethereumTokens = await fetchPopularTokens(ChainFullIds.ETHEREUM);
            tokens.push(...ethereumTokens);
          }

          if (selectedNetwork === "All networks" || selectedNetwork === "Etherlink") {
            const etherlinkTokens = await fetchPopularTokens(ChainFullIds.ETHERLINK);
            tokens.push(...etherlinkTokens);
          }

          setDynamicTokens(tokens);
        } catch (error) {
          console.error("Error loading popular tokens:", error);
        } finally {
          setIsLoadingTokens(false);
        }
      }
    };

    const debounceTimer = setTimeout(() => {
      void searchDynamicTokens();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, selectedNetwork]);

  const resolveEtherlinkIconUrl = useCallback(
    async (token: TokenWithAddress) => {
      if (
        getNetworkChain(token.chainId) === ChainIds.ETHERLINK &&
        token.address &&
        !resolvedIconUrls.has(token.address)
      ) {
        try {
          const provider = registry.getChainProvider(token.chainId);

          const iconUrl = await provider.getAddressIconUrl(token.address);

          if (iconUrl) {
            setResolvedIconUrls(prev => new Map(prev).set(token.address!, iconUrl));
          }
        } catch (error) {
          console.error(`Error resolving icon for ${token.symbol} on Etherlink:`, error);
        }
      }
    },
    [resolvedIconUrls, registry, getNetworkChain]
  );

  // Resolve icons for Etherlink tokens when dynamic tokens change
  useEffect(() => {
    const resolveIcons = async () => {
      const etherlinkTokens = dynamicTokens.filter(
        token =>
          getNetworkChain(token.chainId) === ChainIds.ETHERLINK &&
          token.address &&
          !resolvedIconUrls.has(token.address)
      );

      if (etherlinkTokens.length > 0) {
        for (const token of etherlinkTokens) {
          await resolveEtherlinkIconUrl(token);
        }
      }
    };

    void resolveIcons();
  }, [dynamicTokens, resolvedIconUrls, registry, resolveEtherlinkIconUrl, getNetworkChain]);

  const allTokens = useMemo(() => {
    const unifiedTokens: UnifiedBalanceWithPrices[] = tokens
      .map(token => {
        const symbol = getTokenSymbol(token);

        if (!symbol) {
          return null;
        }

        return {
          chainId: token.chainId,
          symbol,
          balance: token.balance,
          usdPrice: token.usdPrice,
          totalUsdPrice: token.totalUsdPrice,
        };
      })
      .filter((token): token is UnifiedBalanceWithPrices => token !== null);

    const userTokenMap = new Map(
      [...balances, ...unifiedTokens].map(b => [`${b.chainId}-${b.symbol}`, b])
    );

    const mergedTokens = dynamicTokens.map(token => {
      const key = `${token.chainId}-${token.symbol}`;
      const userToken = userTokenMap.get(key);

      if (userToken) {
        return {
          ...token,
          balance: userToken.balance,
          usdPrice: userToken.usdPrice,
          totalUsdPrice: userToken.totalUsdPrice,
        };
      }

      return token;
    });

    const filteredMergedTokens = mergedTokens.filter(token => {
      if (getNetworkChain(token.chainId) === ChainIds.ETHERLINK && !isEtherlinkOnRampEnabled()) {
        return false;
      }

      if (getNetworkChain(token.chainId) === ChainIds.ETHERLINK) {
        return token.symbol === "USDC" || token.symbol === "XTZ";
      }

      return true;
    });

    // Add any user tokens that aren't in dynamic tokens
    const dynamicTokenKeys = new Set(dynamicTokens.map(t => `${t.chainId}-${t.symbol}`));
    const additionalUserTokens = [...balances, ...unifiedTokens].filter(
      b => !dynamicTokenKeys.has(`${b.chainId}-${b.symbol}`)
    );

    // Add default ramp tokens that aren't already included
    const defaultTokenKeys = new Set([
      ...dynamicTokenKeys,
      ...additionalUserTokens.map(t => `${t.chainId}-${t.symbol}`),
    ]);
    const additionalDefaultTokens = DEFAULT_RAMP_TOKENS.filter(
      token => !defaultTokenKeys.has(`${token.chainId}-${token.symbol}`)
    );

    const result = [...filteredMergedTokens, ...additionalUserTokens, ...additionalDefaultTokens];

    result.sort((a, b) => {
      const priorityA = getChainPriority(getNetworkChain(a.chainId));
      const priorityB = getChainPriority(getNetworkChain(b.chainId));

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Within the same chain, prioritize native tokens
      const nativePriorityA = getNativeTokenPriority(getNetworkChain(a.chainId), a.symbol);
      const nativePriorityB = getNativeTokenPriority(getNetworkChain(b.chainId), b.symbol);

      if (nativePriorityA !== nativePriorityB) {
        return nativePriorityA - nativePriorityB;
      }

      return a.symbol.localeCompare(b.symbol);
    });

    return result;
  }, [balances, tokens, dynamicTokens, getNetworkChain]);

  const filteredTokens = useMemo(() => {
    const tokens = allTokens.filter(token => {
      const matchesSearch = token.symbol.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesNetwork =
        selectedNetwork === "All networks" ||
        token.chainId.toLowerCase() === selectedNetwork.toLowerCase();

      return matchesSearch && matchesNetwork;
    });

    return tokens;
  }, [allTokens, searchQuery, selectedNetwork]);

  return (
    <ModalContent>
      <ModalHeader>
        <ModalBackButton />
        <ModalCloseButton />
        <Center flexDirection="column" gap="12px">
          <Heading size="xl">Buy</Heading>
        </Center>
      </ModalHeader>

      <ModalBody>
        <InputGroup marginBottom="16px">
          <InputLeftElement paddingLeft="16px">
            <Icon as={SearchIcon} width="24px" height="24px" color={color("400")} />
          </InputLeftElement>
          <Input
            paddingStart="52px !important"
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search..."
            value={searchQuery}
          />
          <InputRightElement width="auto" paddingRight="2">
            <Menu>
              {({ onClose, isOpen }) => (
                <>
                  <MenuButton
                    as={Button}
                    minWidth="120px"
                    height="fit-content"
                    fontWeight="400"
                    lineHeight="30px"
                    border="0"
                    _hover={{ bg: isOpen ? "gray.100" : "gray.50" }}
                    backgroundColor={isOpen ? "gray.100 !important" : "white"}
                    leftIcon={NETWORK_CONFIGS.find(n => n.id === selectedNetwork)?.icon}
                    rightIcon={<ChevronDownIcon width="20px" height="20px" />}
                  >
                    {selectedNetwork}
                  </MenuButton>
                  <MenuList
                    marginTop="-5px"
                    padding="3"
                    borderColor={color("100")}
                    borderRadius="30px"
                    backgroundColor={color("white")}
                  >
                    <RadioGroup
                      onChange={value => {
                        setSelectedNetwork(value as Network);
                        onClose();
                      }}
                      value={selectedNetwork}
                    >
                      <Stack spacing="2">
                        {NETWORK_CONFIGS.map(network => (
                          <Flex
                            key={network.id}
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                            padding="2"
                            background={
                              selectedNetwork === network.id ? color("100") : "transparent"
                            }
                            borderRadius="100px"
                            _hover={{ bg: "gray.50" }}
                            cursor="pointer"
                            onClick={() => {
                              setSelectedNetwork(network.id);
                              onClose();
                            }}
                          >
                            <Flex alignItems="center" gap="2">
                              {network.icon}
                              <Text fontSize="16px" fontWeight="600">
                                {network.label}
                              </Text>
                            </Flex>
                            <Radio isReadOnly value={network.id} />
                          </Flex>
                        ))}
                      </Stack>
                    </RadioGroup>
                  </MenuList>
                </>
              )}
            </Menu>
          </InputRightElement>
        </InputGroup>
        <VStack gap="12px" overflowY="auto" maxHeight="400px">
          {isLoadingTokens ? (
            <Center padding="40px">
              <Spinner color={color("400")} size="lg" />
            </Center>
          ) : (
            filteredTokens.map(token => {
              const networkConfig = getNetworkConfig(token.chainId);

              if (networkConfig.isTestnet) {
                return null;
              }

              const tokenWithAddress = token as TokenWithAddress;

              let iconUrl = tokenWithAddress.logoURI;

              if (
                getNetworkChain(token.chainId) === ChainIds.ETHERLINK &&
                tokenWithAddress.address
              ) {
                iconUrl = resolvedIconUrls.get(tokenWithAddress.address);
              }

              return (
                <TokenMultichain
                  key={`${token.chainId}-${token.symbol}`}
                  padding="12px"
                  balance={Number(token.balance)}
                  chain={token.chainId}
                  data-testid="multichain-token"
                  iconUrl={iconUrl}
                  name={token.symbol}
                  onClick={() => onClick(token.symbol, token.chainId, iconUrl)}
                  showBalances={Number(token.balance) !== 0}
                  usdBalance={token.totalUsdPrice}
                  usdPrice={token.usdPrice}
                />
              );
            })
          )}
        </VStack>
      </ModalBody>
    </ModalContent>
  );
};
